import { Typography } from 'antd';
import moment from 'moment';

import type {
  BranchUserOption,
  Service,
  ServiceGroup,
  ServiceOption,
  TagOption,
} from '@app/components/RecordModal/components/RecordDetails/types';
import type {
  ClientServiceInterface,
  ModelRecordServiceDiscount,
  OtherRecordsInterface,
  TBranchUser,
  TTag,
  NTags,
} from '@app/types';
import { getContrastYIQ } from '@app/utils/getContrastYIQ';

import type { DefaultOptionType } from 'antd/es/select';

const { Text } = Typography;

/**
 * Sum of every applied discount component on a service line
 * (manual + loyalty + bonuses). Mirrors the RecordPayment tab logic.
 */
export const getServiceDiscountsSum = (
  discounts?: ModelRecordServiceDiscount[] | null
): number =>
  (discounts ?? []).reduce((acc, { Discount }) => acc + (Discount ?? 0), 0);

/**
 * Discount value to display / subtract for a service row.
 * While the form is editable, only the manually entered discount is live, so it
 * is used. When the row is locked (e.g. a loyalty card is applied) the summed
 * total discount is shown instead.
 */
export const getEffectiveServiceDiscount = (
  service: Pick<Service, 'discountCurrency' | 'totalDiscount'> | undefined,
  isLocked?: boolean
): number => {
  if (isLocked) {
    return service?.totalDiscount ?? service?.discountCurrency ?? 0;
  }

  return service?.discountCurrency ?? 0;
};

export const getServicesTotalDuration = (services: Array<Service> = []) => {
  return services.reduce((acc, service) => {
    if (!service?.service) {
      return acc;
    }

    const count = service?.quantity || 0;
    const duration = service?.duration || 0;

    return acc + duration * count;
  }, 0);
};

export const getTotalDurationWithManualDelta = (
  services: Array<Service> = [],
  manualDurationDelta = 0
) => {
  return Math.max(0, getServicesTotalDuration(services) + manualDurationDelta);
};

export const getManualDurationDelta = (
  totalDuration = 0,
  services: Array<Service> = []
) => {
  return totalDuration - getServicesTotalDuration(services);
};

export const getTagsOptions = (tags: Array<TTag> | NTags.Tag[]) => {
  const formattedTagOptions: Array<TagOption> = tags.map(
    ({ Id, Name, Color, IconId }) => ({
      value: Id ?? 0,
      label: Name ?? '',
      color: getContrastYIQ(Color),
      backgroundColor: Color,
      icon: IconId,
    })
  );

  return formattedTagOptions;
};

export const getBransUserOptions = (users: Array<TBranchUser>) => {
  const formattedBransUserOptions: Array<BranchUserOption> = users.map(
    ({ NameFirst, NameLast, UserId }) => ({
      value: UserId,
      label: `${NameFirst ?? ''} ${NameLast ?? ''}`.trim(),
    })
  );

  return formattedBransUserOptions;
};

export const generateTimeOptions = () => {
  const options: Array<DefaultOptionType> = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hh = h.toString().padStart(2, '0');
      const mm = m.toString().padStart(2, '0');
      options.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
    }
  }
  return options;
};

export const generateHourOptions = () => {
  const options: Array<DefaultOptionType> = [];
  for (let i = 0; i < 24; i++) {
    options.push({ value: i, label: `${i} ч` });
  }
  return options;
};

export const generateMinuteOptions = () => {
  const options: Array<DefaultOptionType> = [];
  for (let i = 0; i < 60; i += 5) {
    options.push({ value: i, label: `${i} мин` });
  }
  return options;
};

export const getServicesOptions = (
  services: ClientServiceInterface[]
): Array<ServiceGroup> => {
  const groupedServices = services.reduce((acc, service) => {
    const category = service.Category || 'Без категории';

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(service);
    return acc;
  }, {} as Record<string, ClientServiceInterface[]>);

  const serviceGroups: Array<ServiceGroup> = Object.keys(groupedServices).map(
    (category) => {
      const options: Array<ServiceOption> = groupedServices[category]
        .map((service) => ({
          value: String(service.Id),
          label: service.Name ?? 'Без названия',
          price: service.PriceS ?? 0,
          duration: service.Duration ?? 0,
        }))
        .sort((a, b) => {
          const labelA = a.label ?? '';
          const labelB = b.label ?? '';
          return labelA.localeCompare(labelB);
        });

      return {
        label: category,
        options,
      };
    }
  );
  return serviceGroups.sort((a, b) => a.label.localeCompare(b.label));
};

export const formatDate = (date: string | null): string | null => {
  return date && moment(date).isValid()
    ? moment(date).format('DD.MM.YYYY')
    : null;
};

export const formatAge = (age: number | null | undefined): string | null => {
  if (typeof age !== 'number' || age < 0) return null;

  const years = ['год', 'года', 'лет'];
  const cases = [2, 0, 1, 1, 1, 2];
  const suffix =
    years[age % 100 > 4 && age % 100 < 20 ? 2 : cases[Math.min(age % 10, 5)]];

  return `${age} ${suffix}`;
};

export function rgbToHex(rgbString: string | null) {
  if (!rgbString) {
    return null;
  }

  if (!rgbString.startsWith('rgb')) {
    return rgbString;
  }

  const regex = /rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/;
  const result = regex.exec(rgbString);

  if (!result) {
    return null;
  }
  const r = parseInt(result[1], 10);
  const g = parseInt(result[2], 10);
  const b = parseInt(result[3], 10);

  if ([r, g, b].some((val) => val < 0 || val > 255)) {
    return null;
  }

  const hex = [r, g, b]
    .map((val) => val.toString(16).padStart(2, '0'))
    .join('');

  return `#${hex}`;
}

export const generateEmployeesOptions = (
  employees: OtherRecordsInterface[]
) => {
  return [...employees]
    .sort((a, b) => {
      const timeSComparison =
        moment(a.TimeS).valueOf() - moment(b.TimeS).valueOf();
      if (timeSComparison !== 0) {
        return timeSComparison;
      }

      const timeEComparison =
        moment(a.TimeE).valueOf() - moment(b.TimeE).valueOf();
      if (timeEComparison !== 0) {
        return timeEComparison;
      }

      const nameFirstComparison = (a.NameFirst || '').localeCompare(
        b.NameFirst || ''
      );
      if (nameFirstComparison !== 0) {
        return nameFirstComparison;
      }

      const nameLastComparison = (a.NameLast || '').localeCompare(
        b.NameLast || ''
      );
      if (nameLastComparison !== 0) {
        return nameLastComparison;
      }

      return (a.NameMiddle || '').localeCompare(b.NameMiddle || '');
    })
    .map(
      ({ EmployeeId, TimeE, TimeS, NameFirst, NameLast, NameMiddle, Id }) => {
        const timeS = moment(TimeS).format('HH:mm');
        const timeE = moment(TimeE).format('HH:mm');

        return {
          label: (
            <div className="employee-tabs__tab-label">
              <Text className="employee-tabs__tab-label-title">
                {timeS} - {timeE}
              </Text>
              {Boolean(NameFirst) && (
                <div className="employee-tabs__tab-label-text">{NameFirst}</div>
              )}
              {Boolean(NameLast) && (
                <div className="employee-tabs__tab-label-text">{NameLast}</div>
              )}
              {Boolean(NameMiddle) && (
                <div className="employee-tabs__tab-label-text">
                  {NameMiddle}
                </div>
              )}
            </div>
          ),
          value: `${Id}__${EmployeeId}`,
        };
      }
    );
};
